/** MCP protocol version we negotiate. Streamable-HTTP transport landed 2025-03-26. */
const MCP_PROTOCOL_VERSION = '2025-03-26';
/** Header MCP servers use to bind subsequent requests to a session. */
const MCP_SESSION_HEADER = 'mcp-session-id';
/** How long to wait for an MCP request before aborting. */
const MCP_REQUEST_TIMEOUT_MS = 15_000;
/** Mesh-namespace a tool name so multiple endpoints can advertise the same upstream tool. */
function meshToolName(endpointUrl, toolName) {
    let host;
    try {
        host = new URL(endpointUrl).hostname;
    }
    catch {
        host = endpointUrl.replace(/^https?:\/\//, '').split('/')[0] ?? 'mcp';
    }
    return `mcp:${host}/${toolName}`;
}
async function mcpFetch(url, init) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MCP_REQUEST_TIMEOUT_MS);
    const headers = new Headers(init.headers);
    if (!headers.has('accept')) {
        headers.set('accept', 'application/json, text/event-stream');
    }
    if (!headers.has('content-type') && init.body) {
        headers.set('content-type', 'application/json');
    }
    if (init.sessionId) {
        headers.set(MCP_SESSION_HEADER, init.sessionId);
    }
    try {
        return await fetch(url, { ...init, headers, signal: controller.signal });
    }
    finally {
        clearTimeout(timer);
    }
}
/**
 * Read a JSON-RPC response from a Streamable-HTTP body. Servers may
 * answer with `application/json` (single or batch) or `text/event-stream`.
 */
async function readJsonRpcResponse(response, id) {
    const ct = (response.headers.get('content-type') ?? '').toLowerCase();
    if (ct.includes('text/event-stream')) {
        const text = await response.text();
        for (const block of text.split(/\n\n+/)) {
            const dataLines = block.split('\n').filter((l) => l.startsWith('data:'));
            if (dataLines.length === 0)
                continue;
            const payload = dataLines.map((l) => l.slice(5).trim()).join('');
            if (!payload)
                continue;
            try {
                const parsed = JSON.parse(payload);
                const arr = Array.isArray(parsed) ? parsed : [parsed];
                for (const msg of arr) {
                    if (msg && typeof msg === 'object' && msg.id === id)
                        return msg;
                }
            }
            catch {
                /* skip malformed event */
            }
        }
        throw new Error(`SSE response had no message with id=${id}`);
    }
    const parsed = (await response.json());
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    const match = arr.find((m) => m && typeof m === 'object' && m.id === id);
    if (!match)
        throw new Error(`JSON response had no message with id=${id}`);
    return match;
}
/**
 * Rewrite an `https://host/path` upstream URL through a same-origin
 * proxy. Format: `${proxyBaseUrl}{https|http}/host/path?args`.
 * Matches the regex location the bundled nginx config installs at
 * `/mcp-proxy/` on legion.codecai.net. No-op when `proxyBaseUrl` is
 * falsy — direct fetch (subject to upstream CORS).
 */
export function proxiedMcpUrl(url, proxyBaseUrl) {
    if (!proxyBaseUrl)
        return url;
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return url;
    }
    const scheme = parsed.protocol.replace(/:$/, '');
    if (scheme !== 'https' && scheme !== 'http')
        return url;
    const base = proxyBaseUrl.endsWith('/') ? proxyBaseUrl : `${proxyBaseUrl}/`;
    return `${base}${scheme}/${parsed.host}${parsed.pathname}${parsed.search}`;
}
/**
 * Initialize an MCP session and register every advertised tool in the
 * supplied `ToolRegistry` under `mcp:<host>/<name>`. Idempotent — re-
 * calling with the same URL overwrites previous registrations.
 *
 * Returns the live attachment record on success; throws an `McpError`
 * (typed `kind`) on failure so callers can show useful errors.
 */
export async function discoverMcpEndpoint(url, registry, opts = {}) {
    const fetchUrl = proxiedMcpUrl(url, opts.proxyBaseUrl);
    // Step 1: initialize.
    let initResponse;
    try {
        initResponse = await mcpFetch(fetchUrl, {
            method: 'POST',
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'initialize',
                params: {
                    protocolVersion: MCP_PROTOCOL_VERSION,
                    capabilities: {},
                    clientInfo: { name: 'unstable-legion', version: '0.0.1' },
                },
            }),
        });
    }
    catch (e) {
        const detail = e instanceof Error ? e.message : String(e);
        if (e instanceof DOMException && e.name === 'AbortError') {
            throw { kind: 'timeout', url };
        }
        if (detail.toLowerCase().includes('cors') || detail.toLowerCase().includes('failed to fetch')) {
            throw { kind: 'cors', url, detail };
        }
        throw { kind: 'network', url, detail };
    }
    if (!initResponse.ok) {
        throw { kind: 'protocol', url, detail: `initialize HTTP ${initResponse.status}` };
    }
    const sessionId = initResponse.headers.get(MCP_SESSION_HEADER);
    let initBody;
    try {
        initBody = await readJsonRpcResponse(initResponse, 1);
    }
    catch (e) {
        throw {
            kind: 'protocol',
            url,
            detail: e instanceof Error ? e.message : String(e),
        };
    }
    if (initBody.error) {
        throw {
            kind: 'protocol',
            url,
            detail: `initialize error: ${initBody.error.message}`,
        };
    }
    // Step 2: notifications/initialized — fire-and-forget per MCP spec.
    try {
        await mcpFetch(fetchUrl, {
            method: 'POST',
            sessionId,
            body: JSON.stringify({ jsonrpc: '2.0', method: 'notifications/initialized' }),
        });
    }
    catch {
        /* not all servers require it; best-effort */
    }
    // Step 3: tools/list.
    let listResponse;
    try {
        listResponse = await mcpFetch(fetchUrl, {
            method: 'POST',
            sessionId,
            body: JSON.stringify({ jsonrpc: '2.0', id: 2, method: 'tools/list' }),
        });
    }
    catch (e) {
        throw {
            kind: 'network',
            url,
            detail: e instanceof Error ? e.message : String(e),
        };
    }
    if (!listResponse.ok) {
        throw {
            kind: 'protocol',
            url,
            detail: `tools/list HTTP ${listResponse.status}`,
        };
    }
    const listBody = await readJsonRpcResponse(listResponse, 2);
    if (listBody.error) {
        throw {
            kind: 'protocol',
            url,
            detail: `tools/list error: ${listBody.error.message}`,
        };
    }
    const result = (listBody.result ?? {});
    const upstreamTools = Array.isArray(result.tools) ? result.tools : [];
    // Step 4: register each tool locally.
    const descriptors = [];
    const registeredNames = [];
    for (const t of upstreamTools) {
        if (typeof t.name !== 'string' || !t.name)
            continue;
        const meshName = meshToolName(url, t.name);
        const descriptor = {
            name: meshName,
            description: t.description ?? `(MCP) ${t.name}`,
            inputSchema: t.inputSchema && typeof t.inputSchema === 'object'
                ? t.inputSchema
                : {},
        };
        descriptors.push(descriptor);
        registeredNames.push(meshName);
        registry.register({
            descriptor,
            // Upstream MCP server is the schema authority — no client-side validation here.
            validate: () => null,
            handler: async (args) => {
                const r = await callMcpTool(fetchUrl, sessionId, t.name, args);
                if (!r.ok)
                    throw new Error(r.error);
                return { content: r.content };
            },
        });
    }
    return { url, sessionId, descriptors, registeredNames };
}
/**
 * Invoke an MCP tool via Streamable-HTTP. Used by the registered
 * handler from `discoverMcpEndpoint`.
 */
export async function callMcpTool(endpointUrl, sessionId, upstreamToolName, args) {
    let response;
    try {
        response = await mcpFetch(endpointUrl, {
            method: 'POST',
            sessionId,
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'tools/call',
                params: { name: upstreamToolName, arguments: args },
            }),
        });
    }
    catch (e) {
        return { ok: false, error: `mcp transport: ${e instanceof Error ? e.message : String(e)}` };
    }
    if (!response.ok) {
        return { ok: false, error: `mcp HTTP ${response.status}` };
    }
    let body;
    try {
        body = await readJsonRpcResponse(response, 1);
    }
    catch (e) {
        return { ok: false, error: `mcp response parse: ${e instanceof Error ? e.message : String(e)}` };
    }
    if (body.error) {
        return { ok: false, error: `mcp error: ${body.error.message}` };
    }
    const callResult = (body.result ?? {});
    if (callResult.isError) {
        const txt = callResult.content?.find((c) => c.type === 'text')?.text ?? 'tool reported error';
        return { ok: false, error: txt };
    }
    return { ok: true, content: callResult.content ?? callResult.structuredContent ?? null };
}
/** Detach an MCP endpoint — removes registry entries created at attach. */
export function detachMcpEndpoint(att, registry) {
    for (const name of att.registeredNames)
        registry.unregister(name);
}
